import { ImageOff, MoreHorizontal, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { HistorySection } from '@/features/audit-log';
import { useCountryScope, usePermissions } from '@/features/auth';
import { useSelectedCountry } from '@/features/country-scope';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { NotFoundPage } from '@/shared/components/errors/not-found-page';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

import { useBrandDetails, useSetBrandStatus } from '../api/brands.queries';
import { BrandAttentionPanel } from '../components/brand-attention-panel';
import { BrandDenominations } from '../components/brand-denominations';
import { StorefrontPreviewLinks } from '../components/storefront-preview-links';
import { BrandFormDialog } from '../components/brand-form-dialog';
import { CatalogueChangeDialog } from '../components/catalogue-change-dialog';
import { DeleteBrandDialog } from '../components/delete-catalogue-dialogs';
import type { BrandHeader, CatalogueCountry } from '../types/brand.types';
import { DENOMINATION_SORT_DEFAULTS } from '../types/brand.types';

/**
 * Brand Details — deep-linkable at /brands/:brandId (§54).
 *
 * Everything the business needs to answer "can customers buy this, where,
 * and if not, why not": the brand, what needs attention, and every
 * denomination with its state in the selected country. The states and
 * reasons come from the Admin API; this page only presents them.
 */
export function BrandDetailsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [isEditOpen, setEditOpen] = useState(false);
  const [isStatusOpen, setStatusOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const { country: selectedCountry } = useSelectedCountry();

  const parsedId = Number(brandId);
  const isValidId = Number.isInteger(parsedId) && parsedId > 0;

  // The Denominations table's sort lives in the URL (sort/direction); the
  // Admin API applies it within each region.
  const sortState = useTableSearchParams(DENOMINATION_SORT_DEFAULTS);
  const details = useBrandDetails(isValidId ? parsedId : undefined, {
    sort: sortState.sort === 'name' ? 'name' : 'sortOrder',
    direction: sortState.direction,
  });
  const setStatus = useSetBrandStatus();

  // A non-numeric id in the URL is a bad route, not a failed request.
  if (!isValidId) return <NotFoundPage />;

  if (details.isLoading) return <BrandDetailsSkeleton />;

  if (details.error instanceof ApiError && details.error.isNotFound) return <NotFoundPage />;

  if (details.error) {
    return (
      <PageContainer>
        <ErrorState error={details.error} onRetry={() => void details.refetch()} />
      </PageContainer>
    );
  }

  if (!details.data) return <NotFoundPage />;

  const data = details.data;
  const brand = data.brand;
  const canManage = can('catalogue.manage') && allCountries;

  // The header's selection when this brand's details cover it, else the first country returned.
  const country =
    data.countries.find((candidate) => candidate.id === selectedCountry?.id) ??
    data.countries[0] ??
    null;
  const nameOf = (countryId: number) => catalogueCountryName(data.countries, countryId);

  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/brands', label: 'Brands' }}
        title={
          <span className="flex items-center gap-2.5">
            {brand.name}
            <StatusBadge variant={brand.isActive ? 'success' : 'neutral'}>
              {brand.isActive ? 'Active' : 'Inactive'}
            </StatusBadge>
          </span>
        }
        description={brand.description ?? 'No description set for this brand.'}
        actions={
          canManage ? (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="size-3.5" aria-hidden />
                Edit
              </Button>
              <Button
                size="sm"
                variant={brand.isActive ? 'outline' : 'default'}
                onClick={() => setStatusOpen(true)}
              >
                {brand.isActive ? (
                  <PowerOff className="size-3.5" aria-hidden />
                ) : (
                  <Power className="size-3.5" aria-hidden />
                )}
                {brand.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              {/* Kept in a menu, away from the common actions: deleting is permanent. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" aria-label="More actions">
                    <MoreHorizontal className="size-3.5" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem
                    onSelect={() => setDeleteOpen(true)}
                    className="gap-2 text-xs"
                    variant="destructive"
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    Delete brand
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : undefined
        }
      />

      <StorefrontPreviewLinks
        brandName={brand.name}
        isActive={brand.isActive}
        countries={data.countries}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">Catalogue details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field label="Brand ID" value={<code className="font-mono">{brand.id}</code>} />
              <Field label="Category" value={brand.category.nameEn ?? '—'} />
              <Field
                label="Arabic name"
                value={brand.arName ? <span dir="rtl">{brand.arName}</span> : '—'}
              />
              <Field
                label="Slug"
                value={
                  brand.slug ? (
                    <code className="font-mono text-xs">{brand.slug}</code>
                  ) : (
                    // Nothing reads the slug yet — see the note on the brand form.
                    <span className="text-muted-foreground">Coming soon</span>
                  )
                }
              />
              <Field label="Storefront position" value={brand.sortOrder ?? '—'} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <BrandLogoPanel brand={brand} />
          </CardContent>
        </Card>
      </div>

      <BrandAttentionPanel details={data} countryName={nameOf} shownCountryId={country?.id} />

      {country ? (
        <BrandDenominations details={data} country={country} countryName={nameOf} />
      ) : (
        <p className="text-muted-foreground text-xs">
          No country is assigned to you, so per-country states cannot be shown.
        </p>
      )}

      <HistorySection
        entityType="Brand"
        entityId={String(brand.id)}
        title="Brand history"
        description="Changes to the brand itself — created, edited, activated or deactivated. Each denomination's changes are under the history icon on its row."
      />

      {isEditOpen && <BrandFormDialog open onOpenChange={setEditOpen} brand={brand} />}

      {isDeleteOpen && (
        <DeleteBrandDialog
          brand={brand}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => void navigate('/brands', { replace: true })}
        />
      )}

      {isStatusOpen && (
        <CatalogueChangeDialog
          brandId={brand.id}
          request={{ change: brand.isActive ? 'deactivateBrand' : 'activateBrand' }}
          onOpenChange={setStatusOpen}
          title={brand.isActive ? 'Deactivate this brand?' : 'Activate this brand?'}
          description={
            brand.isActive
              ? `${brand.name} will be hidden from the storefront in every country and its denominations will stop being sold. Existing orders are unaffected.`
              : `${brand.name} will become visible in every country where its denominations are offered.`
          }
          confirmLabel={brand.isActive ? 'Deactivate' : 'Activate'}
          variant={brand.isActive ? 'destructive' : 'default'}
          isPending={setStatus.isPending}
          countryName={nameOf}
          onConfirm={(reason) =>
            setStatus.mutate(
              { brandId: brand.id, isActive: !brand.isActive, reason },
              { onSuccess: () => setStatusOpen(false) },
            )
          }
        />
      )}
    </PageContainer>
  );
}

function catalogueCountryName(countries: CatalogueCountry[], countryId: number): string {
  const country = countries.find((candidate) => candidate.id === countryId);
  return country?.nameEn ?? country?.code ?? `#${countryId}`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </dt>
      <dd className="mt-1 truncate text-sm">{value}</dd>
    </div>
  );
}

function BrandLogoPanel({ brand }: { brand: BrandHeader }) {
  if (!brand.imageUrl) {
    return (
      <div className="bg-muted text-muted-foreground flex aspect-video items-center justify-center rounded-md">
        <div className="text-center">
          <ImageOff className="mx-auto size-6" aria-hidden />
          <p className="mt-2 text-xs">No logo set</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-muted flex aspect-video items-center justify-center rounded-md p-4">
      <img
        src={brand.imageUrl}
        alt={`${brand.name} logo`}
        className="max-h-full max-w-full object-contain"
      />
    </div>
  );
}

function BrandDetailsSkeleton() {
  return (
    <PageContainer>
      <div className="space-y-2">
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
      <Skeleton className="h-72" />
    </PageContainer>
  );
}
